import { Context } from './context'
import { Entity, EntityKey } from './entity'
import errors from './error'
import { QueryBase, Selectables, SelectablesToItem, SerializableQuery } from './query'
import type {
  SourceReadResolution,
  ReadResponse,
  RunOptions,
  SourcesEnhanced,
  SourceEnhanced,
} from './sources/source'
import { intersections } from './utils/types/set'

interface Resolve<
  MainEntityKey extends EntityKey,
  MainEntityKeyAlias extends string,
  Selection extends Selectables = {},
> {
  sources: SourcesEnhanced
  entities: Array<Entity<string, any>>
  query: SerializableQuery<MainEntityKey, MainEntityKeyAlias, Selection>
  context: Context
  onAbort?: () => void
}

export default function resolve<
  MainEntityKey extends EntityKey,
  MainEntityKeyAlias extends string,
  Selection extends Selectables = {},
>({
  sources,
  entities,
  query,
  context,
  onAbort,
}: Resolve<MainEntityKey, MainEntityKeyAlias, Selection>) {
  let isAborted = false
  let hasFinished = false
  let sourceResolution: SourceReadResolution<Selection> | undefined

  const abort = async () => {
    isAborted = true
    if (!sourceResolution || hasFinished) {
      return
    }
    await sourceResolution.abort()
  }

  const run = async ({
    onResponse = () => undefined,
  }: Partial<RunOptions<ReadResponse<Selection>>> = {}) => {
    if (isAborted) {
      throw errors.abort()
    }

    const entityKeys = extractEntityKeys(query)
    const entitiesToVerify = verifyEntities(entityKeys, entities)
    const targetEntity = entitiesToVerify[0]
    const sourceCandidates = findSourceCandidates(entitiesToVerify, sources)

    if (!sourceCandidates.length) {
      throw errors.incompatible(`Não há suporte para sources cruzadas no momento`)
    }

    const sourceEntries = Object.entries(sources)
    const validSourceEntries = sourceEntries.filter(
      (entry): entry is [string, SourceEnhanced] => entry[1] !== undefined,
    )
    const oltpSource = await checkOltpSource(validSourceEntries, context, query)

    if (oltpSource) {
      return await queryTargetSource('oltp', oltpSource, query, context, onResponse, onAbort)
    }

    for (const [name, targetSource] of sourceEntries) {
      if (targetSource && (await isSourceAvailable(targetSource, context))) {
        try {
          return await queryTargetSource(name, targetSource, query, context, onResponse, onAbort)
        } catch (err) {
          continue
        }
      }
    }

    throw errors.incompatible(`Fontes de dados "${targetEntity.sources}" indisponíveis`)
  }

  return { abort, run }
}

const extractEntityKeys = (query: QueryBase): Set<string> => {
  const entityKeys = new Set<string>()
  const addQueryEntities = (currentQuery: QueryBase) => {
    entityKeys.add(currentQuery.entityKey)
    currentQuery.currentJoins.forEach(join => entityKeys.add(join.current.entityKey))
    currentQuery.currentJoins.forEach(join => entityKeys.add(join.target.entityKey))
    currentQuery.currentUnions?.forEach(addQueryEntities)
  }
  addQueryEntities(query)
  return entityKeys
}

const verifyEntities = (
  entityKeys: Set<string>,
  entities: Array<Entity<string, any>>,
): Array<Entity<string, any, string[]>> => {
  const entityKeysArray = Array.from(entityKeys)
  const candidateEntities = entityKeysArray.map(
    key => entities.find(entity => entity.key === key) || null,
  )
  if (candidateEntities.includes(null)) {
    throw errors.incompatible(`Uma entidade "${entityKeysArray.join(', ')}" está indisponível`)
  }
  return candidateEntities as Array<Entity<string, any, string[]>>
}

const findSourceCandidates = (
  entitiesToVerify: Array<Entity<string, any, string[]>>,
  sources: SourcesEnhanced,
): SourceEnhanced[] => {
  const entitySources = entitiesToVerify.map(
    entity =>
      entity.sources.map(sourceKey => sources[sourceKey]).filter(Boolean) as SourceEnhanced[],
  )
  return intersections(...entitySources)
}

const checkOltpSource = async (
  sourceEntries: [string, SourceEnhanced][],
  context: Context,
  query: QueryBase,
) => {
  //Checar custo da query em OLTP
  const [_, targetSource] = sourceEntries.find(item => item[0] === 'oltp') ?? []
  if (
    targetSource &&
    (await isSourceAvailable(targetSource, context)) &&
    targetSource.cost !== undefined
  ) {
    const oltpCost = await getOltpCost(targetSource, context, query)
    const minCost = 150 * 1000
    if (oltpCost && oltpCost < minCost) {
      return targetSource
    }
  }
  return null
}

const getOltpCost = async (
  source: SourceEnhanced,
  context: Context,
  query: QueryBase,
): Promise<number> => {
  const sourceResolution = await source.cost({ query, context })
  return new Promise((resolve, reject) => {
    sourceResolution
      .run({
        onResponse: (response: any) => {
          resolve(response.items[0]['QUERY PLAN'][0]['Plan']['Total Cost'])
        },
      })
      .catch(reject)
  })
}

const isSourceAvailable = async (source: SourceEnhanced, context: Context): Promise<boolean> => {
  return source && (await source.checkAvailability({ context }))
}

const queryTargetSource = async (
  name: string,
  targetSource: SourceEnhanced,
  query: QueryBase,
  context: Context,
  onResponse: (response: ReadResponse<any>) => void,
  onAbort?: () => void,
) => {
  let sourceResolution: SourceReadResolution<any> | undefined
  try {
    sourceResolution = await targetSource.read({
      query,
      context,
      onAbort: () => {
        if (onAbort) onAbort()
      },
    })

    const currentResponse = await new Promise<any>((resolve, reject) => {
      sourceResolution
        ?.run({
          onResponse: response => {
            if (name === 'olap') {
              response.items = response.items.map(convertItemToJSON)
            }
            onResponse(response)
          },
        })
        .then(result => {
          resolve(result)
        })
        .catch(reject)
    })

    return { firstResponse: currentResponse }
  } catch (err) {
    console.error(err)
    throw err
  }
}

const convertItemToJSON = <Selection>(
  item: Record<string, unknown>,
): SelectablesToItem<Selection> => {
  const newItem: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(item)) {
    try {
      newItem[key] = JSON.parse(value as string)
    } catch (err) {
      newItem[key] = value
    }
  }
  return newItem as SelectablesToItem<Selection>
}
